import React, { useEffect, useRef, useState } from 'react';
import styles from './Services.module.css';
import gsap from 'gsap';
import axios from 'axios';
import { ServicesType } from '../../utils/Types/ServicesTypes';
import { useNavigate } from 'react-router-dom';

export type ServicesProps = {
    services: ServicesType[];
    setServices: React.Dispatch<React.SetStateAction<ServicesType[]>>;
    currentService: number | null;
    setCurrentService: React.Dispatch<React.SetStateAction<number | null>>;
    setNavBackground: React.Dispatch<React.SetStateAction<boolean>>;
};

const Services = (props: ServicesProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [lockScroll, setLockScroll] = useState(true);
    const [currentSection, setCurrentSection] = useState(0);
    const [scrolling, setScrolling] = useState(false);
    const navigate = useNavigate();
    const overlayRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        if (!props.services || props.services.length === 0) return;

        overlayRefs.current.forEach((overlay) => {
            if (!overlay) return;

            gsap.fromTo(
                overlay,
                { scale: 1, transformOrigin: 'center center' },
                {
                    scale: 1.1,
                    duration: 10,
                    ease: 'power1.inOut',
                    yoyo: true,
                    repeat: -1,
                }
            );
        });
    }, [props.services]);

    // Fetch services
    useEffect(() => {
        props.setNavBackground(false);

        axios
            .get('https://sazuli.pt/api/services')
            .then((response) => props.setServices(response.data))
            .catch((error) => console.log(error));
    }, []);

    // Initial panel setup
    useEffect(() => {
        if (containerRef.current) {
            const firstChild = containerRef.current.children[0] as HTMLElement;
            if (firstChild) firstChild.style.transform = 'translateY(0)';
        }
    }, [props.services]);

    // Handle currentService jump
    useEffect(() => {
        if (props.currentService === null || !containerRef.current) return;

        const children = Array.from(containerRef.current.children) as HTMLElement[];
        const index = children.findIndex((el) => el.getAttribute('data-id') === String(props.currentService));

        if (index === -1 || index === currentSection || scrolling) return;

        const target = children[index];
        const current = children[currentSection];

        setScrolling(true);

        gsap.to(current, {
            y: index > currentSection ? '-100%' : '100%',
            duration: 2,
            ease: 'power3.inOut',
        });

        gsap.to(target, {
            y: '0%',
            duration: 2,
            ease: 'power3.inOut',
            onComplete: () => {
                setCurrentSection(index);
                setScrolling(false);
            },
        });

        props.setCurrentService(null);
    }, [props.currentService]);

    // Scroll panel animations
    useEffect(() => {
        const onWheel = (e: WheelEvent) => {
            if (!containerRef.current) return;

            const elements = containerRef.current.children.length - 1;
            const currentEl = containerRef.current.children[currentSection] as HTMLElement;
            const rect = currentEl.getBoundingClientRect();
            const tolerance = 100;
            const isFullyInView = rect.top >= -tolerance && rect.bottom <= window.innerHeight + tolerance;

            const isLast = currentSection === elements;
            const hasNext = currentSection + 1 <= elements && e.deltaY > 0;
            const hasPrev = currentSection - 1 >= 0 && e.deltaY < 0;

            if (scrolling) {
                e.preventDefault();
                return;
            }

            if (!isFullyInView) return;

            if (isLast && !hasPrev) setLockScroll(false);
            else setLockScroll(true);

            if (lockScroll) e.preventDefault();

            if (hasNext && !scrolling && lockScroll) {
                setScrolling(true);
                const next = containerRef.current.children[currentSection + 1] as HTMLElement;
                const prev = containerRef.current.children[currentSection] as HTMLElement;

                gsap.to(prev, { y: '-100%', duration: 2, ease: 'power3.inOut' });
                gsap.to(next, {
                    y: '0%',
                    duration: 2,
                    ease: 'power3.inOut',
                    onComplete: () => {
                        setCurrentSection(currentSection + 1);
                        setScrolling(false);
                    },
                });
            }

            if (hasPrev && !scrolling && lockScroll) {
                setScrolling(true);
                const next = containerRef.current.children[currentSection - 1] as HTMLElement;
                const prev = containerRef.current.children[currentSection] as HTMLElement;

                gsap.to(prev, { y: '100%', duration: 2, ease: 'power3.inOut' });
                gsap.to(next, {
                    y: '0%',
                    duration: 2,
                    ease: 'power3.inOut',
                    onComplete: () => {
                        setCurrentSection(currentSection - 1);
                        setScrolling(false);
                    },
                });
            }
        };

        window.addEventListener('wheel', onWheel, { passive: false });
        return () => window.removeEventListener('wheel', onWheel);
    }, [currentSection, lockScroll, scrolling]);

    const sidePanel = (index: number) => index % 2 === 0;

    // Handle button redirect
    const handleRedirect = (item: ServicesType) => {
        if (!item.RedirectBtn) return;

        if (item.RedirectPos === true || item.RedirectBtn.startsWith('http')) {
            window.open(item.RedirectBtn, '_blank');
        } else {
            navigate(item.RedirectBtn.startsWith('/') ? item.RedirectBtn : `/${item.RedirectBtn}`);
        }
    };

    return (
        <div className={styles.container} ref={containerRef}>
            <div className={styles.video}>
                <video src="https://sazuli.pt/uploads/Serviços.mp4" autoPlay loop muted />
                <h1 className={styles.textOverlay}>
                    Estamos ao seu lado
                    <br />
                    em cada etapa da
                    <br />
                    transformação da sua casa
                </h1>
            </div>

            {props.services.map((item, index) => (
                <div className={styles.panel} key={index} data-id={item.ID}>
                    <div
                        ref={(el) => {
                            overlayRefs.current[index] = el; // assign to ref
                        }}
                        className={styles.overlay}
                        style={{ backgroundImage: `url(https://sazuli.pt/uploads/${item.Image})` }}
                    />
                    <div className={`${styles.item} ${sidePanel(index) ? styles.left : styles.right}`}>
                        <h2>{item.Title}</h2>
                        <p>{item.Text}</p>
                        <button className={styles.button} onClick={() => handleRedirect(item)}>
                            {item.RedirectText}
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default Services;